"use client";

import { INTENTIONS, type IntentionId } from "./content";
import { trackLandingEvent } from "./analytics";
import Image from "next/image";
import { useEffect, useRef } from "react";

function HeroVideo() {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
    let visible = true;
    const update = () => {
      if (motion.matches || connection?.saveData) {
        video.pause(); video.removeAttribute("src"); video.load(); return;
      }
      if (!video.getAttribute("src")) video.src = "/videos/ceremonies-hero-720p.mp4";
      if (visible && !document.hidden) void video.play().catch(() => {});
      else video.pause();
    };
    const observer = typeof IntersectionObserver !== "undefined" ? new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; update(); }) : null;
    observer?.observe(video);
    motion.addEventListener("change", update);
    document.addEventListener("visibilitychange", update);
    update();
    return () => { observer?.disconnect(); motion.removeEventListener("change", update); document.removeEventListener("visibilitychange", update); video.pause(); };
  }, []);
  return <video ref={ref} className="hero-video" muted loop playsInline preload="none" poster="/images/landing/wedding-hero-poster.jpg" aria-hidden="true" />;
}

export function HeroTwoLives() {
  return (
    <section className="hero-scene" aria-labelledby="hero-title">
      <HeroVideo />
      <div className="scene-inner hero-copy">
        <h1 id="hero-title">Dating should<br />lead <em>somewhere.</em></h1>
        <p className="hero-lede">Meet people whose intentions, values, faith and direction in life may genuinely align with yours.</p>
        <div className="hero-actions">
          <a href="#intention" className="magnetic-cta" onClick={() => trackLandingEvent("hero_cta_selected")}>Meet with intention <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M5 12h14M13 6l6 6-6 6" /></svg></a>
        </div>
      </div>
    </section>
  );
}

export function IntentionSelector({ selected, onSelect }: { selected: IntentionId | null; onSelect: (id: IntentionId) => void }) {
  const current = INTENTIONS.find((item) => item.id === selected);
  const image = selected === "companionship" ? "/images/landing/cafe-900.webp" : selected === "marriage" ? "/images/landing/gallery-700.webp" : "/images/landing/zainab-800.webp";
  return (
    <section id="intention" className="landing-section intention-section" aria-labelledby="intention-title">
      <div className="scene-inner intention-layout">
        <div className="intention-copy"><p className="landing-kicker">Begin with honesty</p>
        <h2 id="intention-title">What are you hoping to find?</h2>
        <div className="intention-options" role="group" aria-label="Choose an intention for this page experience">
          {INTENTIONS.map((item, index) => (
            <button key={item.id} type="button" aria-pressed={selected === item.id} onClick={() => onSelect(item.id)}>
              <span>0{index + 1}</span>{item.label}<i aria-hidden="true">↗</i>
            </button>
          ))}
        </div>
        <div className="intention-response" aria-live="polite">
          {current ? <><span>You chose</span><strong>{current.label}</strong><p>{current.response}</p></> : <p>Choose if you would like the story to respond to you. You can also continue without selecting anything.</p>}
        </div></div>
        <figure className="intention-photograph"><Image key={image} src={image} width={900} height={1100} sizes="(max-width: 640px) 100vw, 60vw" alt={selected === "companionship" ? "Two adults in conversation at a café" : selected === "marriage" ? "People taking time together in an art gallery" : "A woman in a quiet moment by a window"} /><figcaption>Photography changes with the direction you choose. Demonstration only.</figcaption></figure>
      </div>
    </section>
  );
}
